import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, getFirstAllowedRoute } from '../utils/permissions';

export const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission }) => {
  const { user, loading, logout, mustChangePassword } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // Safety timeout: if loading takes >3s, something is stuck — force logout
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setTimedOut(true), 3000);
    return () => clearTimeout(timer);
  }, [loading]);

  // When timeout fires, call logout in useEffect (not during render)
  useEffect(() => {
    if (timedOut && loading) {
      logout();
    }
  }, [timedOut, loading, logout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'superadmin' && !user.email_verified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Superadmin granular permission check
  if (requiredPermission && user.role === 'superadmin' && !hasPermission(user, requiredPermission)) {
    return <Navigate to={getFirstAllowedRoute(user)} replace />;
  }

  return children;
};
